(() => {
  "use strict";

  const root = document.getElementById("af-overlay-menu-root");
  if (!root) return;
  if (root.__afInit) return;
  root.__afInit = true;

  /* iOS touch fix styles moved to af-overlay-menu.css to avoid the forced
     reflow caused by appending a <style> element on script execution. */

  /* =========================================================
     Config
  ========================================================= */
  const LOCK_CLASS = "af-overlay-lock";
  const OPEN_CLASS = "is-open";

  // Mobile back/gesture history token (no URL change — prevents false GA4/Meta page views)
  const HIST_TOKEN = `af_om_${Math.random().toString(36).slice(2)}`;

  const cfg = {
    minChars: Number(root.dataset.afOmMinChars || 1),

    // ✅ Align fallbacks with Liquid schema defaults
    limitProducts: Number(root.dataset.afOmPredictiveProducts || 8),
    limitCollections: Number(root.dataset.afOmPredictiveCollections || 6),

    limitPages: Number(root.dataset.afOmPredictivePages || 3),
    limitArticles: Number(root.dataset.afOmPredictiveArticles || 3),
    limitQueries: Number(root.dataset.afOmPredictiveQueries || 4),
    fullSectionId: String(root.dataset.afOmFullSectionsId || "main-search"),

    // Full results
    fullLimit: 24,
    fullCollectionsLimit: 12,

    // Recent searches
    recentMax: 8,
    recentKey: "af_om_recent_queries_v1",

    // Required UX/perf knobs
    debounceMs: 250,
    hoverIntentMs: 100,

    // Predictive cache (required)
    cacheTTL: 60000,
    cacheMax: 50,
  };

  const routes = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes : {};
  const SEARCH_URL = routes.search_url || "/search";

  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const $$ = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));

  const nodes = {
    body: $(".af-om__body"),

    modeMenu: $('[data-af-om-mode="menu"]'),
    modePredictive: $('[data-af-om-mode="predictive"]'),
    modeFull: $('[data-af-om-mode="full"]'),

    form: $("[data-af-om-search-form]"),
    input: $("[data-af-om-search-input]"),
    clear: $("[data-af-om-clear]"),

    predictiveWrap: $("[data-af-om-predictive]"),
    predictiveTitle: $("[data-af-om-results-title]"),

    fullWrap: $("[data-af-om-full]"),
    fullTitle: $("[data-af-om-full-title]"),

    backBtns: $$( "[data-af-om-back]" ),
    closeBtns: $$( "[data-af-overlay-close]" ),

    navCol: $("[data-af-om-nav]"),
    subCol: $("[data-af-om-subnav]"),
    promosCol: $("[data-af-om-promos]"),

    topbar: $(".af-om__top"),
    progWrap: $("[data-af-scrollprog], .af-om__scrollProg"),
    progFill: $("[data-af-scrollprogfill], .af-om__scrollProgFill"),
  };

  /* =========================================================
     State
  ========================================================= */
  const state = {
    level: "none",
    groupKey: null,

    parentKey: null,
    childKey: null,

    parentUrl: null,
    childUrl: null,

    mobileSubviewOpen: false,
    suppressFocus: false,

    lastQuery: "",
    inflight: null,
    debounceT: 0,

    lastMode: "menu",

    returnFocusEl: null,
    hideT: 0,

    movedPromos: [],

    // Root promo: memoized "brands you viewed" tiles (null = not yet resolved)
    brandTiles: null,

    // hash/history bookkeeping
    hashArmed: false,
    subviewHistPushed: false,

    // listeners armed only while open (perf)
    listenersArmed: false,

    // scroll progress tracking
    activeScrollEl: null,
    scrollBound: false,
    rafProg: 0,
    lastScrollTarget: null,
  };

  let _suppressNextPop = false;

  const mqDesktop = window.matchMedia("(min-width: 990px)");
  const isDesktop = () => mqDesktop.matches;

  /* =========================================================
     iOS TOUCH FIX — canHover() is false on all Apple touch
     devices (iPhone, iPad without a paired mouse/trackpad).
     It uses the (hover: hover) and (pointer: fine) media query,
     which only matches real pointer devices like a mouse.
     On iPad landscape (≥990px), isDesktop() is true but
     canHover() is still false — so hover-intent timers are
     correctly skipped and only taps/clicks open submenus.
  ========================================================= */
  const mqHover = window.matchMedia("(hover: hover) and (pointer: fine)");
  const canHover = () => mqHover.matches;

  /* =========================================================
     Predictive cache (in-memory, TTL) — REQUIRED
  ========================================================= */
  const _predictiveCache = new Map(); // qLower -> { t, data }

  const getCachedPredictive = (q) => {
    const k = String(q || "").trim().toLowerCase();
    if (!k) return null;
    const hit = _predictiveCache.get(k);
    if (!hit) return null;
    if ((Date.now() - hit.t) > cfg.cacheTTL) {
      _predictiveCache.delete(k);
      return null;
    }
    return hit.data || null;
  };

  const setCachedPredictive = (q, data) => {
    const k = String(q || "").trim().toLowerCase();
    if (!k) return;
    if (_predictiveCache.has(k)) _predictiveCache.delete(k);
    _predictiveCache.set(k, { t: Date.now(), data });

    while (_predictiveCache.size > cfg.cacheMax) {
      const firstKey = _predictiveCache.keys().next().value;
      if (!firstKey) break;
      _predictiveCache.delete(firstKey);
    }
  };

  /* =========================================================
     Collection banner data (parsed lazily from Liquid JSON)

     #af-om-collection-banners is ~21KB of JSON emitted on EVERY page by
     sections/af-overlay-menu.liquid. It is only ever needed once the user
     opens the overlay menu and hovers a group, so parsing it eagerly at
     script load spent ~21KB of main-thread JSON.parse on every page view
     for data most visitors never use.

     Parsed on first access instead, then memoised. `null` means "not yet
     parsed"; a failed or missing lookup memoises `{}` so we never retry.
  ========================================================= */
  let _collectionBanners = null;

  const loadCollectionBanners = () => {
    if (_collectionBanners) return _collectionBanners;
    try {
      const el = document.getElementById("af-om-collection-banners");
      _collectionBanners = el ? (JSON.parse(el.textContent) || {}) : {};
    } catch { _collectionBanners = {}; }
    return _collectionBanners;
  };

  const getCollectionBanner = (parentKey) => {
    const k = normalizeKey(parentKey);
    return k ? (loadCollectionBanners()[k] || null) : null;
  };

  // Build a responsive srcset from a Shopify CDN banner url (which carries
  // ?width=700 from the Liquid). Keeps the dynamic promos crisp like the
  // static image_tag tiles instead of a single upscaled 700px source.
  const bannerSrcset = (url) => {
    if (!url) return "";
    const withW = (w) =>
      /[?&]width=\d+/.test(url)
        ? url.replace(/([?&])width=\d+/, `$1width=${w}`)
        : url + (url.includes("?") ? "&" : "?") + "width=" + w;
    return [400, 600, 800, 1000].map((w) => `${withW(w)} ${w}w`).join(", ");
  };

  // Search result thumbnails: product originals run 100-280 KB each. Ask the
  // CDN for small widths instead (2 columns on phones, 4 on desktop).
  const thumbAttrs = (url) => {
    const withW = (w) =>
      /[?&]width=\d+/.test(url)
        ? url.replace(/([?&])width=\d+/, `$1width=${w}`)
        : url + (url.includes("?") ? "&" : "?") + "width=" + w;
    const srcset = [240, 360, 480].map((w) => `${withW(w)} ${w}w`).join(", ");
    return `src="${esc(withW(240))}" srcset="${esc(srcset)}" sizes="(min-width: 990px) 180px, 45vw"`;
  };

  const removeDynamicPromos = () => {
    root.querySelectorAll("[data-af-dynamic-promo]").forEach((el) => el.remove());
  };

  const buildDynamicPromoEl = (data) => {
    const wrap = document.createElement("a");
    wrap.className = "af-om__promo";
    wrap.href = data.url || "#";
    wrap.setAttribute("data-af-dynamic-promo", "");
    wrap.setAttribute("data-af-om-promo", "");
    wrap.innerHTML = `
      <div class="af-om__promoMedia">
        <img class="af-om__promoImg" src="${esc(data.image)}" srcset="${esc(bannerSrcset(data.image))}" sizes="(min-width: 990px) 400px, 90vw" alt="${esc(data.title)}" loading="lazy" decoding="async">
        <div class="af-om__promoOverlay" aria-hidden="true"></div>
        <div class="af-om__promoCopy">
          <div class="af-om__promoTL">
            <div class="af-om__promoTitle">${esc(data.title)}</div>
          </div>
          <div class="af-om__promoBR">
            <span class="af-om__promoCta">Shop Now</span>
          </div>
        </div>
      </div>`;
    return wrap;
  };

  /* =========================================================
     Root promo: "Brands you viewed"
     Most shoppers browse individual perfumes, not collections — so the
     resting menu panel showcases the BRANDS behind the shopper's recently
     viewed products (deduped, most-recent first, up to 2), each linking to
     that brand's collection. New shoppers fall back to a curated trio.
     Brand → collection is the handleized vendor, matched against the same
     collection-banners JSON the hover promos use.
  ========================================================= */
  const RECENT_VIEWED_KEY = "af_recently_viewed_handles";
  const FALLBACK_BRANDS = ["amouage", "nishane", "louis-vuitton"];

  const handleizeVendor = (v) =>
    String(v || "")
      .toLowerCase()
      .trim()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const readRecentViewed = () => {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_VIEWED_KEY) || "[]");
      return Array.isArray(v) ? v.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  };

  // Resolve up to 2 brand tiles ({image,title,url}); memoized for the page.
  const resolveBrandTiles = async () => {
    if (state.brandTiles) return state.brandTiles;
    const tiles = [];
    const seen = new Set();
    const pushKey = (key) => {
      const k = normalizeKey(key);
      if (!k || seen.has(k) || tiles.length >= 2) return;
      const b = getCollectionBanner(k);
      if (b && b.image) {
        seen.add(k);
        tiles.push(b);
      }
    };
    const handles = readRecentViewed().slice(0, 6);
    if (handles.length) {
      try {
        const products = await Promise.all(
          handles.map((h) =>
            fetch("/products/" + encodeURIComponent(h) + ".js", {
              credentials: "same-origin",
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        products.forEach((p) => {
          if (p && p.vendor) pushKey(handleizeVendor(p.vendor));
        });
      } catch {}
    }
    // Fill remaining slots with the curated fallback brands.
    FALLBACK_BRANDS.forEach((k) => pushKey(k));
    state.brandTiles = tiles;
    return tiles;
  };

  const renderRootBrandPromos = () => {
    if (!nodes.promosCol || !Array.isArray(state.brandTiles)) return;
    state.brandTiles.forEach((t) =>
      nodes.promosCol.appendChild(buildDynamicPromoEl(t))
    );
  };

  /* =========================================================
     Utilities
  ========================================================= */
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const normalizeKey = (k) => (k || "").toString().trim().toLowerCase();

  const normalizeUrl = (u) => {
    try {
      if (!u) return "";
      const url = new URL(String(u), window.location.origin);
      let p = url.pathname || "";
      if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
      return p.toLowerCase();
    } catch {
      let s = String(u || "").split("?")[0].split("#")[0];
      if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1).toLowerCase();
      return s.toLowerCase();
    }
  };

  const domFromHTML = (html) => {
    const parser = new DOMParser();
    return parser.parseFromString(String(html || ""), "text/html");
  };

  const getMoneyFormat = () => {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function" && window.Shopify.money_format) {
      return window.Shopify.money_format;
    }
    return null;
  };

  const formatMoneyCents = (cents) => {
    const n = typeof cents === "number" ? cents : Number(cents);
    if (!Number.isFinite(n)) return "";

    const mf = getMoneyFormat();
    if (mf && window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try {
        const formatted = window.Shopify.formatMoney(n, mf);
        if (formatted && !formatted.match(/[৳$€£¥₹]/)) return `৳ ${formatted}`;
        return formatted;
      } catch {}
    }

    const codeGuess =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.iso_code) ||
      "BDT";

    const code = (/^[A-Z]{3}$/).test(String(codeGuess || "")) ? String(codeGuess) : "BDT";

    try {
      // "en-US", not the browser locale: a Bangla-locale browser got Bengali numerals.
      // Whole taka, like the rest of the site.
      const formatted = new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(n / 100);
      return formatted.replace(/BDT\s?/g, "৳ ").replace(/\.00$/, "").trim();
    } catch {
      return `৳ ${(n / 100).toFixed(2)}`;
    }
  };

  const formatPriceRange = (p) => {
    const toCents = (v) => {
      if (v === null || v === undefined) return null;
      if (typeof v === "number" && Number.isFinite(v)) return v;

      const s = String(v).trim();
      if (!s) return null;

      const cleaned = s
        .replace(/৳|Tk|BDT|USD|EUR|GBP|JPY|INR/gi, "")
        .replace(/[, ]+/g, "")
        .trim();

      if (!cleaned) return null;

      if (cleaned.includes(".")) {
        const f = Number.parseFloat(cleaned);
        return Number.isFinite(f) ? Math.round(f * 100) : null;
      }

      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    };

    // N3/SHOP-02: "From <min>" instead of a range, matching price.liquid's card_price branch.
    if (p?.variants && Array.isArray(p.variants) && p.variants.length) {
      const cents = p.variants.map((v) => toCents(v?.price)).filter((x) => x !== null);
      if (cents.length) {
        const min = Math.min(...cents);
        const max = Math.max(...cents);
        return max > min ? `From ${formatMoneyCents(min)}` : formatMoneyCents(min);
      }
    }

    const minC = toCents((p && (p.price_min ?? p.price ?? p.price_min_cents ?? p.price_cents)) ?? null);
    const maxC = toCents((p && (p.price_max ?? p.price_max_cents)) ?? null);

    if (minC !== null && maxC !== null && maxC > minC) return `From ${formatMoneyCents(minC)}`;
    if (minC !== null) return formatMoneyCents(minC);

    if (p?.price && typeof p.price === "string") {
      const priceStr = p.price.trim();
      if (priceStr.match(/[৳$€£¥₹Tk]/)) return priceStr;
      return `৳ ${priceStr}`;
    }

    return "";
  };

  /* =========================================================
     History (gesture-safe) — NO URL CHANGE
     pushState without URL prevents GA4/Meta false page_view events.
     Back gesture detected via popstate (not hashchange).
  ========================================================= */
  const isOurHistState = () => {
    try { return !!(history.state && history.state.__af === HIST_TOKEN); } catch { return false; }
  };

  const armHash = () => {
    if (isDesktop()) return;
    if (isOurHistState()) {
      state.hashArmed = true;
      return;
    }
    try {
      history.pushState({ __af: HIST_TOKEN, level: 1 }, "");
      state.hashArmed = true;
    } catch {
      state.hashArmed = false;
    }
  };

  const pushSubviewHist = () => {
    if (isDesktop()) return;
    if (state.subviewHistPushed) return;
    try {
      history.pushState({ __af: HIST_TOKEN, level: 2 }, "");
      state.subviewHistPushed = true;
    } catch {}
  };

  const disarmHashByBack = () => {
    if (isDesktop()) return false;
    if (!state.hashArmed) return false;
    if (!isOurHistState()) return false;

    try {
      history.back();
      return true;
    } catch {
      return false;
    }
  };

  const onPopState = () => {
    if (!isOpen()) return;
    if (isDesktop()) return;

    if (_suppressNextPop) {
      _suppressNextPop = false;
      state.subviewHistPushed = false;
      return;
    }

    // If subview is open, close it first (user pressed back once)
    if (state.mobileSubviewOpen) {
      closeMobileSubview(true);
      state.subviewHistPushed = false;
      return;
    }

    // Our state was popped — close the entire overlay
    if (!isOurHistState()) {
      closeInternal(true);
    }
  };

  // No cleanup needed — no URL was changed
  const cleanupHash = () => {
    state.hashArmed = false;
    state.subviewHistPushed = false;
  };

  /* =========================================================
     Scroll lock + open/close
  ========================================================= */
  const lockScroll = () => {
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
  };

  const isOpen = () => root.classList.contains(OPEN_CLASS);

  const setMode = (mode) => {
    if (!nodes.modeMenu || !nodes.modePredictive || !nodes.modeFull) return;

    nodes.modeMenu.hidden = mode !== "menu";
    nodes.modePredictive.hidden = mode !== "predictive";
    nodes.modeFull.hidden = mode !== "full";

    state.lastMode = mode;

    if (mode === "predictive" || mode === "full") closeMobileSubview(true);

    for (const b of nodes.backBtns) {
      b.hidden = (mode === "menu");
    }

    requestAnimationFrame(() => pickBestScrollerAndUpdate());
  };

  /* =========================================================
     Promo targeting
  ========================================================= */
  const allPromoTiles = () => $$("[data-af-promo], [data-af-om-promo]", root);

  const tileScope = (el) => normalizeKey(el.getAttribute("data-af-scope") || "global");
  const tileTargetKey = (el) => normalizeKey(el.getAttribute("data-af-target") || "");
  const tileTargetUrl = (el) => normalizeUrl(el.getAttribute("data-af-target-url") || "");

  const matchItem = (el, key, url) => {
    if (tileScope(el) !== "item") return false;

    const tUrl = tileTargetUrl(el);
    if (tUrl && url && tUrl === normalizeUrl(url)) return true;

    const tKey = tileTargetKey(el);
    if (tKey && key && tKey === normalizeKey(key)) return true;

    return false;
  };

  const matchGroup = (el, groupKey) => {
    if (tileScope(el) !== "group") return false;
    const t = tileTargetKey(el);
    return !!t && !!groupKey && t === normalizeKey(groupKey);
  };

  const matchGlobal = (el) => tileScope(el) === "global";

  const setPromosForActiveState = () => {
    removeDynamicPromos();

    const tiles = allPromoTiles();
    const groupKey = normalizeKey(state.groupKey);

    let itemKey = "";
    let itemUrl = "";

    if (state.level === "child") {
      itemKey = normalizeKey(state.childKey);
      itemUrl = normalizeUrl(state.childUrl);
    } else if (state.level === "parent" || state.level === "main") {
      itemKey = normalizeKey(state.parentKey);
      itemUrl = normalizeUrl(state.parentUrl);
    }

    const itemSet = (itemKey || itemUrl) ? tiles.filter((t) => matchItem(t, itemKey, itemUrl)) : [];
    const groupSet = groupKey ? tiles.filter((t) => matchGroup(t, groupKey)) : [];
    const globalSet = tiles.filter((t) => matchGlobal(t));

    let show = globalSet;
    let hasManualItemOrGroup = false;

    if (state.level === "main") {
      show = itemSet.length ? itemSet : globalSet;
      hasManualItemOrGroup = itemSet.length > 0;
    } else if (state.level === "parent" || state.level === "child") {
      if (itemSet.length) {
        show = itemSet;
        hasManualItemOrGroup = true;
      } else if (groupSet.length) {
        show = groupSet;
        hasManualItemOrGroup = true;
      } else {
        show = globalSet;
      }
    } else {
      show = globalSet;
    }

    const showSet = new Set(show);
    tiles.forEach((t) => {
      t.style.display = showSet.has(t) ? "" : "none";
    });

    if (
      (state.level === "parent" || state.level === "child") &&
      !hasManualItemOrGroup
    ) {
      let banner = null;
      if (state.level === "child" && state.childKey) {
        banner = getCollectionBanner(state.childKey);
      }
      if (!banner && state.parentKey) {
        banner = getCollectionBanner(state.parentKey);
      }
      // Parent/group is not itself a collection with a banner → fall back to
      // its first child collection's banner so the promo panel is never empty
      // while browsing a group (e.g. hovering "Fragrance Family/Notes").
      if (!banner && state.parentKey) {
        const tpl = findTemplate(state.parentKey);
        const firstChild =
          tpl && tpl.content
            ? tpl.content.querySelector("[data-af-om-subitem][data-af-key]")
            : null;
        const fck = firstChild && firstChild.getAttribute("data-af-key");
        if (fck) banner = getCollectionBanner(fck);
      }

      if (banner && banner.image) {
        const promoHost = nodes.promosCol;
        if (promoHost) {
          promoHost.prepend(buildDynamicPromoEl(banner));
        }
      }
    }

    // Resting state (nothing hovered): showcase "brands you viewed" when no
    // manual global/item tile is configured. Tiles are resolved async on open.
    if (
      (state.level === "none" || state.level === "main") &&
      !globalSet.length &&
      !itemSet.length &&
      Array.isArray(state.brandTiles) &&
      state.brandTiles.length
    ) {
      renderRootBrandPromos();
    }
  };

  const resetPromosToDefault = () => {
    removeDynamicPromos();
    state.level = "none";
    state.groupKey = null;
    state.parentKey = null;
    state.childKey = null;
    state.parentUrl = null;
    state.childUrl = null;
    setPromosForActiveState();
  };

  /* =========================================================
     Submenu system
  ========================================================= */
  const findTemplate = (key) => root.querySelector('template[data-af-om-submenu="' + CSS.escape(key) + '"]');

  const markActiveParent = (parentKey) => {
    $$("[data-af-om-parent][data-af-key]").forEach((btn) => {
      const k = btn.getAttribute("data-af-key");
      const on = !!parentKey && k === parentKey;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-expanded", on ? "true" : "false");
    });
  };

  const renderSubmenuIntoColumn = (parentKey) => {
    if (!nodes.subCol) return;

    nodes.subCol.innerHTML = "";
    const tpl = findTemplate(parentKey);
    if (!tpl) {
      nodes.subCol.innerHTML = '<div class="af-om__subEmpty" data-af-om-sub-empty>Select a category</div>';
      root.classList.remove("af-om--has-active-submenu");
      return;
    }
    nodes.subCol.appendChild(tpl.content.cloneNode(true));
    nodes.subCol.setAttribute("data-af-active-parent", parentKey);
    root.classList.add("af-om--has-active-submenu");

    requestAnimationFrame(() => pickBestScrollerAndUpdate());
  };

  const clearSubmenuUI = (keepParentActive = false) => {
    if (nodes.subCol) {
      nodes.subCol.innerHTML = '<div class="af-om__subEmpty" data-af-om-sub-empty>Select a category</div>';
      nodes.subCol.removeAttribute("data-af-active-parent");
      root.classList.remove("af-om--has-active-submenu");
    }
    if (!keepParentActive) markActiveParent(null);
  };

  /* =========================================================
     Mobile subview
  ========================================================= */
  const initSwipeGesture = (el) => {
    if (!el || el.__afSwipeInit) return;
    el.__afSwipeInit = true;

    let sx = 0;
    let sy = 0;
    let tracking = false;

    el.addEventListener("touchstart", (e) => {
      try {
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        sx = t.clientX;
        sy = t.clientY;
        tracking = (sx < window.innerWidth * 0.4);
      } catch {}
    }, { passive: true });

    el.addEventListener("touchend", (e) => {
      try {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;

        const dx = t.clientX - sx;
        const dy = t.clientY - sy;

        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        if (dx <= 60) return;

        const angle = Math.atan2(ady, adx) * 180 / Math.PI;
        if (angle < 30) closeMobileSubview(false);
      } catch {}
    }, { passive: true });
  };

  const ensureMobileSubview = () => {
    let el = root.querySelector("[data-af-om-mobile-subview]");
    if (el) return el;

    el = document.createElement("div");
    el.className = "af-om__mobile-subview";
    el.setAttribute("data-af-om-mobile-subview", "");
    el.innerHTML = `
      <div class="af-om__mobile-subview-bar">
        <button type="button" class="af-om__mback" data-af-om-mobile-back aria-label="Back">
          <span class="af-om__mbackIcon" aria-hidden="true">‹</span>
          <span class="af-om__mbackLabel">Back</span>
        </button>
        <div class="af-om__mobile-subview-title" data-af-om-mobile-title></div>
      </div>
      <div class="af-om__mobile-subview-body" data-af-om-mobile-body></div>
    `;

    const host = nodes.body || nodes.modeMenu || root;
    host.appendChild(el);

    el.querySelector("[data-af-om-mobile-back]")?.addEventListener("click", (e) => {
      e.preventDefault();
      closeMobileSubview(false);
    });

    initSwipeGesture(el);

    return el;
  };

  const restoreMovedPromos = () => {
    const oldWrap = root.querySelector('[data-af-om-mobile-promos]');
    if (oldWrap) oldWrap.remove();

    if (!state.movedPromos || !state.movedPromos.length) return;

    state.movedPromos.forEach((item) => {
      try {
        if (!item || !item.node || !item.placeholder) return;

        if (item.node.hasAttribute("data-af-dynamic-promo")) {
          const ph = item.placeholder;
          if (ph && ph.parentNode) ph.parentNode.removeChild(ph);
          return;
        }

        const ph = item.placeholder;
        const parent = ph.parentNode;
        if (!parent) return;
        parent.insertBefore(item.node, ph);
        parent.removeChild(ph);
      } catch {}
    });

    state.movedPromos = [];
  };

  const moveVisiblePromosInto = (targetEl) => {
    if (!targetEl) return;

    restoreMovedPromos();

    const promos = allPromoTiles().filter((t) => t.style.display !== "none");
    if (!promos.length) return;

    const wrap = document.createElement("div");
    wrap.className = "af-om__mobile-promos";
    wrap.setAttribute("data-af-om-mobile-promos", "");

    promos.forEach((p) => {
      try {
        const ph = document.createComment("af-om-promo-ph");
        if (p.parentNode) p.parentNode.insertBefore(ph, p);
        wrap.appendChild(p);
        state.movedPromos.push({ node: p, placeholder: ph });
      } catch {}
    });

    targetEl.appendChild(wrap);
  };

  const openMobileSubview = (parentKey, titleText) => {
    const subview = ensureMobileSubview();
    const body = subview.querySelector("[data-af-om-mobile-body]");
    const title = subview.querySelector("[data-af-om-mobile-title]");

    if (title) title.textContent = titleText || "";
    body.innerHTML = "";

    const tpl = findTemplate(parentKey);
    if (tpl) {
      const frag = tpl.content.cloneNode(true);
      const head = frag.querySelector(".af-om__subHead");
      if (head) head.remove();
      body.appendChild(frag);
    }

    setPromosForActiveState();
    moveVisiblePromosInto(body);

    root.classList.add("af-om--mobile-subview-open");
    state.mobileSubviewOpen = true;

    pushSubviewHist();
    requestAnimationFrame(() => pickBestScrollerAndUpdate());
    // a11y: move focus into the subview (its Back button), or keyboard and screen-reader
    // users stay on the now-covered parent list
    subview.querySelector("[data-af-om-mobile-back]")?.focus({ preventScroll: true });
  };

  const closeMobileSubview = (skipHistoryBack) => {
    const subviewEl = root.querySelector("[data-af-om-mobile-subview]");
    const hadFocus = !!subviewEl && subviewEl.contains(document.activeElement);
    root.classList.remove("af-om--mobile-subview-open");
    state.mobileSubviewOpen = false;

    restoreMovedPromos();

    state.childKey = null;
    state.childUrl = null;
    if (state.level === "child") state.level = "parent";
    setPromosForActiveState();

    const subview = root.querySelector("[data-af-om-mobile-subview]");
    if (subview) {
      const title = subview.querySelector("[data-af-om-mobile-title]");
      if (title) title.textContent = "";
      const body = subview.querySelector("[data-af-om-mobile-body]");
      if (body) body.innerHTML = "";
    }

    // a11y: back to the parent that opened it
    if (hadFocus && state.parentKey && isOpen()) {
      root
        .querySelector('[data-af-om-parent][data-af-key="' + CSS.escape(state.parentKey) + '"]')
        ?.focus({ preventScroll: true });
    }

    if (!skipHistoryBack && !isDesktop() && state.subviewHistPushed) {
      _suppressNextPop = true;
      state.subviewHistPushed = false;
      try { history.back(); } catch {}
    }

    requestAnimationFrame(() => pickBestScrollerAndUpdate());
  };

  const setActiveParent = (btn) => {
    const parentKey = btn.getAttribute("data-af-key");
    const groupKey = btn.getAttribute("data-af-group");
    const parentUrl = btn.getAttribute("data-af-url") || btn.dataset.afUrl;

    const label = (btn.textContent || "").trim();
    if (!parentKey) return;

    state.level = "parent";
    state.groupKey = groupKey || null;
    state.parentKey = parentKey || null;
    state.parentUrl = parentUrl || null;

    state.childKey = null;
    state.childUrl = null;

    markActiveParent(parentKey);
    setPromosForActiveState();

    if (isDesktop()) renderSubmenuIntoColumn(parentKey);
    else openMobileSubview(parentKey, label);
  };

  const setActiveMainLink = (linkEl) => {
    const href = linkEl.getAttribute("href") || "";
    const itemKey = linkEl.getAttribute("data-af-key") || "";

    state.level = "main";
    state.groupKey = null;
    state.parentKey = itemKey || null;
    state.parentUrl = href || null;

    state.childKey = null;
    state.childUrl = null;

    clearSubmenuUI(false);
    closeMobileSubview(true);

    setPromosForActiveState();
  };

  /* =========================================================
     Focus trap — ARMED ONLY WHILE OPEN (perf)
  ========================================================= */
  const getFocusableEls = () => {
    const sel = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const els = $$(sel, root);
    return els.filter((el) => el && el.getClientRects && el.getClientRects().length > 0);
  };

  const trapTabKey = (e) => {
    if (e.key !== "Tab") return;
    if (!isOpen()) return;

    const focusables = getFocusableEls();
    if (!focusables.length) return;

    const active = document.activeElement;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (!root.contains(active)) {
      e.preventDefault();
      first.focus({ preventScroll: true });
      return;
    }

    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  };

  const onKeydownWhileOpen = (e) => {
    if (!isOpen()) return;

    if (e.key === "Escape") {
      if (!isDesktop() && state.mobileSubviewOpen) {
        e.preventDefault();
        closeMobileSubview(false);
        return;
      }
      close();
      return;
    }

    if (e.key === "Tab") trapTabKey(e);
  };

  const armOpenListeners = () => {
    if (state.listenersArmed) return;
    state.listenersArmed = true;

    window.addEventListener("popstate", onPopState);
    document.addEventListener("keydown", onKeydownWhileOpen, true);
  };

  const disarmOpenListeners = () => {
    if (!state.listenersArmed) return;
    state.listenersArmed = false;

    window.removeEventListener("popstate", onPopState);
    document.removeEventListener("keydown", onKeydownWhileOpen, true);
  };

  /* =========================================================
     Promo video control — REQUIRED
  ========================================================= */
  const playPromoVideos = () => {
    const vids = Array.from(root.querySelectorAll("video.af-om__promoVideo"));
    vids.forEach((v) => {
      try {
        v.preload = "metadata";
        v.muted = true;
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {}
    });
  };

  const pausePromoVideos = () => {
    const vids = Array.from(root.querySelectorAll("video.af-om__promoVideo"));
    vids.forEach((v) => {
      try { v.pause(); } catch {}
    });
  };

  /* =========================================================
     Promo image preloading — trigger on overlay open
  ========================================================= */
  const preloadPromoImages = () => {
    root.querySelectorAll('.af-om__promoImg[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
    });
  };

  /* =========================================================
     Scroll progress bar (no-scrollbar UX)
  ========================================================= */
  const ensureProgressEls = () => {
    if (!nodes.topbar) return;
    let wrap = nodes.progWrap;
    let fill = nodes.progFill;
    if (!wrap || !fill) {
      wrap = document.createElement("div");
      wrap.className = "af-om__scrollProg";
      wrap.setAttribute("data-af-scrollprog", "");
      fill = document.createElement("span");
      fill.className = "af-om__scrollProgFill";
      fill.setAttribute("data-af-scrollprogfill", "");
      wrap.appendChild(fill);
      nodes.topbar.appendChild(wrap);
    }
    nodes.progWrap = wrap;
    nodes.progFill = fill;
    nodes.progWrap.removeAttribute("hidden");
  };

  const canScroll = (el) => {
    if (!el || el.nodeType !== 1) return false;
    const ch = el.clientHeight || 0;
    if (ch <= 0) return false;
    return el.scrollHeight > ch + 1;
  };

  const setProgressIdle = () => {
    const wrap = nodes.progWrap;
    if (!wrap) return;
    wrap.removeAttribute("hidden");
    wrap.classList.add("is-idle");
    wrap.style.setProperty("--af-prog", "0%");
  };

  const setProgressFrom = (el) => {
    const wrap = nodes.progWrap;
    if (!wrap) return;
    wrap.removeAttribute("hidden");
    if (!el || !canScroll(el)) {
      setProgressIdle();
      return;
    }
    const max = el.scrollHeight - el.clientHeight;
    const p = max <= 0 ? 0 : Math.max(0, Math.min(1, el.scrollTop / max));
    wrap.classList.remove("is-idle");
    wrap.style.setProperty("--af-prog", `${(p * 100).toFixed(2)}%`);
  };

  const scheduleProgress = (scroller) => {
    state.lastScrollTarget = scroller || state.lastScrollTarget;
    if (state.rafProg) return;
    state.rafProg = requestAnimationFrame(() => {
      state.rafProg = 0;
      const el = state.lastScrollTarget;
      if (el && canScroll(el)) {
        state.activeScrollEl = el;
        setProgressFrom(el);
      } else {
        pickBestScrollerAndUpdate();
      }
    });
  };

  const pickBestScrollerAndUpdate = () => {
    if (state.lastMode === "predictive" && nodes.modePredictive) {
      if (canScroll(nodes.modePredictive)) {
        state.activeScrollEl = nodes.modePredictive;
        setProgressFrom(nodes.modePredictive);
        return;
      }
    }
    if (state.lastMode === "full" && nodes.modeFull) {
      if (canScroll(nodes.modeFull)) {
        state.activeScrollEl = nodes.modeFull;
        setProgressFrom(nodes.modeFull);
        return;
      }
    }

    if (!isDesktop() && nodes.body && canScroll(nodes.body)) {
      state.activeScrollEl = nodes.body;
      setProgressFrom(nodes.body);
      return;
    }

    const candidates = [];
    if (nodes.subCol) candidates.push(nodes.subCol);
    if (nodes.navCol) candidates.push(nodes.navCol);
    if (nodes.promosCol) candidates.push(nodes.promosCol);

    const scroller = candidates.find((el) => canScroll(el));
    if (!scroller) {
      state.activeScrollEl = null;
      setProgressIdle();
      return;
    }
    state.activeScrollEl = scroller;
    setProgressFrom(scroller);
  };

  const canScrollInDir = (el, dy) => {
    if (!el || !canScroll(el)) return false;
    const top = el.scrollTop;
    const max = el.scrollHeight - el.clientHeight;
    if (dy < 0) return top > 0;
    if (dy > 0) return top < max;
    return false;
  };

  /* =========================================================
     ✅ Robust "inside scroller" detection (composedPath)
     This prevents mobile scroll from being killed.
  ========================================================= */
  const SCROLLER_SEL =
    ".af-om__panel, .af-om__mode--menu, .af-om__subnav, .af-om__nav, .af-om__promos, .af-om__mode--predictive, .af-om__mode--full, .af-om__body, .af-om__mobile-subview-body, .af-om__mobile-subview";

  const findScrollerFromEvent = (e) => {
    const path = (e && typeof e.composedPath === "function") ? e.composedPath() : null;

    if (path && Array.isArray(path)) {
      for (const n of path) {
        if (!(n instanceof Element)) continue;
        if (n.matches && n.matches(SCROLLER_SEL)) return n;
        if (n.closest) {
          const c = n.closest(SCROLLER_SEL);
          if (c) return c;
        }
      }
    }

    let t = e && e.target;
    if (t && t.nodeType !== 1) t = t.parentElement;
    if (t && t.closest) return t.closest(SCROLLER_SEL);

    return null;
  };

  const bindScrollUX = () => {
    if (state.scrollBound) return;
    state.scrollBound = true;

    ensureProgressEls();

    root.addEventListener("scroll", (e) => {
      if (!isOpen()) return;
      const t = e.target;
      if (t && t.nodeType === 1 && canScroll(t)) scheduleProgress(t);
    }, true);

    root.addEventListener("wheel", (e) => {
      if (!isOpen()) return;
      if (e.ctrlKey) return; // let pinch/ctrl-wheel zoom through

      const insideScroller = findScrollerFromEvent(e);
      if (insideScroller) {
        if (!canScrollInDir(insideScroller, e.deltaY)) {
          e.preventDefault();
          scheduleProgress(insideScroller);
        }
        return;
      }

      const s = state.activeScrollEl;
      if (!s || !canScroll(s)) {
        e.preventDefault();
        setProgressIdle();
        return;
      }
      e.preventDefault();
      const d =
        e.deltaMode === 1
          ? e.deltaY * 16
          : e.deltaMode === 2
          ? e.deltaY * s.clientHeight
          : e.deltaY;
      s.scrollTop += d;
      scheduleProgress(s);
    }, { passive: false });

    root.addEventListener("touchmove", (e) => {
      if (!isOpen()) return;
      if (!e.cancelable) return;

      const inside = findScrollerFromEvent(e);
      if (inside) return;

      e.preventDefault();
    }, { passive: false });
  };

  /* =========================================================
     Skeleton HTML — REQUIRED
  ========================================================= */
  const buildSkeletonHTML = () => {
    const chips = `
      <div class="af-om__rsec">
        <div class="af-om__rsecTitle af-om__skeleton-pulse">
          <span class="af-om__skeleton-bar" style="display:inline-block;width:120px;height:10px;"></span>
        </div>
        <div class="af-om__chips">
          <div class="af-om__skeleton-chip af-om__skeleton-pulse"></div>
          <div class="af-om__skeleton-chip af-om__skeleton-pulse"></div>
          <div class="af-om__skeleton-chip af-om__skeleton-pulse"></div>
        </div>
      </div>`;

    const products = `
      <div class="af-om__rsec">
        <div class="af-om__rsecTitle af-om__skeleton-pulse">
          <span class="af-om__skeleton-bar" style="display:inline-block;width:90px;height:10px;"></span>
        </div>
        <div class="af-om__pgrid">
          ${Array.from({ length: 4 }).map(() => `
            <div class="af-om__pcard af-om__pcard--skeleton">
              <div class="af-om__pmedia af-om__skeleton-pulse"></div>
              <div class="af-om__pinfo">
                <div class="af-om__skeleton-bar af-om__skeleton-pulse" style="height:10px;width:90%;margin-top:10px;"></div>
                <div class="af-om__skeleton-bar af-om__skeleton-pulse" style="height:10px;width:55%;margin-top:8px;"></div>
              </div>
            </div>
          `).join("")}
        </div>
      </div>`;

    return `<div class="af-om__results">${chips}${products}</div>`;
  };

  /* =========================================================
     OPEN/CLOSE
  ========================================================= */
  const HIDE_DELAY_MS = 560;

  const focusOnOpenTarget = () => {
    try {
      if (isDesktop() && nodes.input) {
        nodes.input.focus({ preventScroll: true });
        return;
      }
      const closeBtn = root.querySelector("[data-af-overlay-close]");
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    } catch {}
  };

  const openInternal = () => {
    if (state.hideT) {
      window.clearTimeout(state.hideT);
      state.hideT = 0;
    }

    root.hidden = false;

    state.returnFocusEl = document.querySelector("[data-af-overlay-toggle]") || document.activeElement || null;

    state.suppressFocus = true;
    if (nodes.input) nodes.input.value = "";
    if (nodes.clear) nodes.clear.hidden = true;

    closeMobileSubview(true);
    clearSubmenuUI(true);
    resetPromosToDefault();

    // Resolve "brands you viewed" tiles, then re-render the root promo if the
    // shopper is still at the resting state (hasn't hovered a category yet).
    resolveBrandTiles().then((tiles) => {
      if (tiles && tiles.length && (state.level === "none" || state.level === "main")) {
        removeDynamicPromos();
        setPromosForActiveState();
      }
    });

    state.subviewHistPushed = false;

    setMode("menu");

    root.classList.add(OPEN_CLASS);
    document.documentElement.classList.add("af-om-open");

    const t = document.querySelector("[data-af-overlay-toggle]");
    if (t) t.setAttribute("aria-expanded", "true");

    lockScroll();

    armOpenListeners();
    armHash();

    requestAnimationFrame(() => {
      if (!isDesktop()) setTimeout(focusOnOpenTarget, 120);
      else focusOnOpenTarget();
    });

    setTimeout(() => { state.suppressFocus = false; }, 80);

    playPromoVideos();
    preloadPromoImages();

    bindScrollUX();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => pickBestScrollerAndUpdate());
    });
  };

  const closeInternal = (fromBackGesture) => {
    pausePromoVideos();

    root.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove("af-om-open");

    const t = document.querySelector("[data-af-overlay-toggle]");
    if (t) t.setAttribute("aria-expanded", "false");

    unlockScroll();

    closeMobileSubview(true);
    clearSubmenuUI(true);
    resetPromosToDefault();

    if (nodes.input) nodes.input.value = "";
    if (nodes.clear) nodes.clear.hidden = true;
    state.lastQuery = "";

    state.subviewHistPushed = false;

    const rf = state.returnFocusEl;
    state.returnFocusEl = null;
    if (rf && typeof rf.focus === "function") {
      setTimeout(() => { try { rf.focus({ preventScroll: true }); } catch {} }, 0);
    }

    if (state.hideT) window.clearTimeout(state.hideT);
    state.hideT = window.setTimeout(() => {
      state.hideT = 0;
      if (!isOpen()) root.hidden = true;
    }, HIDE_DELAY_MS);

    // ✅ Clean up hash if user closed normally (not via back gesture)
    if (!fromBackGesture) cleanupHash();

    // ✅ Reset history flags
    state.hashArmed = false;
    state.subviewHistPushed = false;

    disarmOpenListeners();
  };

  const open = () => openInternal();

  const close = () => {
    if (!isDesktop() && state.mobileSubviewOpen) {
      closeMobileSubview(false);
      return;
    }

    if (!isDesktop() && state.hashArmed) {
      disarmHashByBack();
    }
    closeInternal(false);
  };

  // How many of our entries sit on top of the page's own (1 = menu, 2 = submenu)
  const ourHistDepth = () => {
    if (isDesktop() || !isOurHistState()) return 0;
    return Number(history.state.level || 1);
  };

  // The × closes the whole menu, even from a submenu, and drops all our entries.
  // closeInternal runs first, so onPopState sees a closed menu and does nothing.
  const closeAll = () => {
    const n = ourHistDepth();
    closeInternal(false);
    if (n) {
      try { history.go(-n); } catch {}
    }
  };

  const toggle = () => (isOpen() ? close() : open());

  const prevApi = window.AfOverlayMenu;
  const api = { open, close, toggle };
  window.AfOverlayMenu = api;

  try {
    if (prevApi && prevApi.__afStub && Array.isArray(prevApi._q) && prevApi._q.length) {
      const q = prevApi._q.splice(0);
      q.forEach((item) => {
        const name = item && item[0];
        const args = item && item[1];
        const fn = api && api[name];
        if (typeof fn !== "function") return;
        try { fn.apply(null, args ? Array.from(args) : []); } catch {}
      });
    }
  } catch {}

  document.addEventListener("keydown", (e) => {
    const k = (e.key || "").toLowerCase();
    if (!((e.metaKey || e.ctrlKey) && k === "k")) return;
    e.preventDefault();
    if (!isOpen()) open();
    requestAnimationFrame(() => {
      try { nodes.input && nodes.input.focus({ preventScroll: true }); } catch {}
    });
  });

  root.addEventListener("click", (e) => {
    const x = e.target.closest("[data-af-overlay-close]");
    if (!x) return;
    e.preventDefault();
    closeAll();
  }, true);

  const goBack = () => {
    if (!isDesktop() && state.mobileSubviewOpen) {
      closeMobileSubview(false);
      return;
    }

    if (nodes.modeFull && nodes.modeFull.hidden === false) {
      const q = (nodes.input ? nodes.input.value.trim() : "");
      if (q) { setMode("predictive"); return; }
      setMode("menu");
      return;
    }

    if (nodes.modePredictive && nodes.modePredictive.hidden === false) {
      if (nodes.input) nodes.input.value = "";
      if (nodes.clear) nodes.clear.hidden = true;
      state.lastQuery = "";
      setMode("menu");
      return;
    }

    setMode("menu");
  };

  root.addEventListener("click", (e) => {
    const back = e.target.closest("[data-af-om-back]");
    if (!back) return;
    e.preventDefault();
    goBack();
  }, true);

  let hoverTimer = 0;

  // iOS TOUCH FIX: cancel any pending hover re-render the moment
  // a tap begins, so it cannot fire between touchstart and click.
  root.addEventListener("pointerdown", () => {
    window.clearTimeout(hoverTimer);
  }, true);

  root.addEventListener("pointerenter", (e) => {
    if (!isDesktop()) return;
    // iOS TOUCH FIX: skip hover-intent entirely on touch devices.
    // On iPhone/iPad, pointerenter fires on tap and would trigger
    // setActiveParent mid-tap, wiping the submenu the user is clicking.
    if (!canHover()) return;
    const btn = e.target.closest("[data-af-om-parent][data-af-key]");
    if (!btn) return;
    window.clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout(() => setActiveParent(btn), cfg.hoverIntentMs);
  }, true);

  root.addEventListener("focusin", (e) => {
    if (!isDesktop()) return;
    const btn = e.target.closest("[data-af-om-parent][data-af-key]");
    if (!btn) return;
    setActiveParent(btn);
  });

  // a11y (desktop): the submenu column sits after the whole nav in the DOM, so Tab alone
  // walked every parent (each focus swapping the column) and never reached a submenu.
  // Enter/Space/ArrowRight on a parent moves focus into its column; ArrowLeft, Shift+Tab
  // from the first item or Tab from the last returns to the nav.
  const subColItems = () =>
    nodes.subCol
      ? $$("a[href], button:not([disabled])", nodes.subCol).filter((el) => el.getClientRects().length)
      : [];

  const focusSubmenu = () => {
    const first = subColItems()[0];
    if (first) first.focus({ preventScroll: true });
  };

  root.addEventListener("keydown", (e) => {
    if (!isDesktop() || e.defaultPrevented || !nodes.subCol) return;
    const btn = e.target.closest("[data-af-om-parent][data-af-key]");
    if (btn && e.key === "ArrowRight") {
      e.preventDefault();
      setActiveParent(btn);
      focusSubmenu();
      return;
    }
    if (!nodes.subCol.contains(e.target)) return;
    const parent = root.querySelector("[data-af-om-parent][data-af-key].is-active");
    if (!parent) return;
    const items = subColItems();
    const i = items.indexOf(e.target.closest("a, button"));
    if (e.key === "ArrowLeft" || (e.key === "Tab" && e.shiftKey && i === 0)) {
      e.preventDefault();
      parent.focus({ preventScroll: true });
    } else if (e.key === "Tab" && !e.shiftKey && i === items.length - 1) {
      const navItems = $$("a[href], button:not([disabled])", nodes.navCol || root).filter((el) => el.getClientRects().length);
      const next = navItems[navItems.indexOf(parent) + 1];
      if (next) {
        e.preventDefault();
        next.focus({ preventScroll: true });
      }
    }
  });

  // iOS TOUCH FIX: e.stopPropagation() removed — on iOS Safari it
  // was preventing the click from completing navigation after the
  // overlay's capture-phase handler processed it.
  root.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-af-om-parent][data-af-key]");
    if (!btn) return;
    e.preventDefault();
    setActiveParent(btn);
    // Enter/Space arrive as a click with detail 0
    if (isDesktop() && e.detail === 0) focusSubmenu();
  }, true);

  const activateChild = (a) => {
    const childKey = a.getAttribute("data-af-key") || "";
    const childUrl = a.getAttribute("href") || "";
    if (!childKey && !childUrl) return;

    const g = a.getAttribute("data-af-group");
    if (g) state.groupKey = g;

    state.level = "child";
    state.childKey = childKey || null;
    state.childUrl = childUrl || null;

    setPromosForActiveState();
  };

  root.addEventListener("pointerenter", (e) => {
    // iOS TOUCH FIX: skip on touch devices to avoid tap side-effects
    if (!canHover()) return;
    const a = e.target.closest("[data-af-om-subitem][data-af-key]");
    if (!a) return;
    activateChild(a);
  }, true);

  root.addEventListener("focusin", (e) => {
    const a = e.target.closest("[data-af-om-subitem][data-af-key]");
    if (!a) return;
    activateChild(a);
  });

  root.addEventListener("pointerenter", (e) => {
    if (!isDesktop()) return;
    // iOS TOUCH FIX: skip on touch devices
    if (!canHover()) return;

    const link = e.target.closest('.af-om__link:not(.af-om__link--parent)');
    if (!link) return;
    if (link.hasAttribute("data-af-om-subitem")) return;
    const inMainNav = link.closest("[data-af-om-nav]");
    if (!inMainNav) return;

    setActiveMainLink(link);
  }, true);

  root.addEventListener("focusin", (e) => {
    if (!isDesktop()) return;

    const link = e.target.closest('.af-om__link:not(.af-om__link--parent)');
    if (!link) return;
    if (link.hasAttribute("data-af-om-subitem")) return;
    const inMainNav = link.closest("[data-af-om-nav]");
    if (!inMainNav) return;

    setActiveMainLink(link);
  });

  /* =========================================================
     Search: predictive + full
  ========================================================= */
  const getRecent = () => {
    try {
      const raw = localStorage.getItem(cfg.recentKey);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  };

  const saveRecent = (q) => {
    const v = String(q || "").trim();
    if (!v) return;
    const cur = getRecent();
    const next = [v, ...cur.filter((x) => x.toLowerCase() !== v.toLowerCase())].slice(0, cfg.recentMax);
    try { localStorage.setItem(cfg.recentKey, JSON.stringify(next)); } catch {}
  };

  const renderRecentSuggestions = () => {
    if (!nodes.predictiveWrap || !nodes.input) return;

    const q = nodes.input.value.trim();
    if (q.length) return;

    const recent = getRecent();
    const chips = recent.length
      ? `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Recent searches</div>
          <div class="af-om__chips">
            ${recent.map((x) =>
              `<button type="button" class="af-om__chip af-om__chip--btn" data-af-om-recent="${esc(x)}">${esc(x)}</button>`
            ).join("")}
          </div>
        </div>
      `
      : '<div class="af-om__loading">Search begins at 1 character.</div>';

    if (nodes.predictiveTitle) nodes.predictiveTitle.textContent = "Search";
    nodes.predictiveWrap.innerHTML = chips;
  };

  root.addEventListener("click", (e) => {
    const b = e.target.closest("[data-af-om-recent]");
    if (!b || !nodes.input) return;
    e.preventDefault();
    const v = b.getAttribute("data-af-om-recent") || "";
    nodes.input.value = v;
    nodes.input.focus({ preventScroll: true });
    onQueryChange(v);
  });

  root.addEventListener("click", (e) => {
    const b = e.target.closest("[data-af-om-suggestion]");
    if (!b || !nodes.input) return;
    e.preventDefault();
    const v = b.getAttribute("data-af-om-suggestion") || "";
    if (!v) return;
    nodes.input.value = v;
    nodes.input.dispatchEvent(new Event("input", { bubbles: true }));
  });

  const fetchPredictive = async (q) => {
    const cached = getCachedPredictive(q);
    if (cached) return cached;

    if (state.inflight && typeof state.inflight.abort === "function") state.inflight.abort();
    state.inflight = new AbortController();

    const predictiveUrl = (routes && routes.predictive_search_url)
      ? routes.predictive_search_url
      : `${SEARCH_URL}/suggest`;

    const url =
      `${predictiveUrl}` +
      `?q=${encodeURIComponent(q)}` +
      `&resources[type]=product,collection,page,article,query` +
      // limit caps at 10, and without limit_scope=each it is shared by every type,
      // which left room for only ~4 products. Ask only for as many as the menu shows.
      `&resources[limit]=${Math.min(10, Math.max(cfg.limitProducts, cfg.limitCollections, cfg.limitPages, cfg.limitArticles, cfg.limitQueries))}` +
      `&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=hide` +
      `&resources[options][fields]=title,product_type,variants.title,vendor,tag`;

    const res = await fetch(url, {
      signal: state.inflight.signal,
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error(`predictive fetch failed (${res.status})`);

    const data = await res.json();
    setCachedPredictive(q, data);
    return data;
  };

  const renderPredictive = (q, data) => {
    if (!nodes.predictiveWrap) return;

    const resources = data?.resources?.results || {};

    const products = (resources.products || []).slice(0, cfg.limitProducts);
    const collections = (resources.collections || []).slice(0, cfg.limitCollections);
    const pages = (resources.pages || []).slice(0, cfg.limitPages);
    const articles = (resources.articles || []).slice(0, cfg.limitArticles);
    const queries = (resources.queries || []).slice(0, cfg.limitQueries);

    const hasAny =
      (products && products.length) ||
      (collections && collections.length) ||
      (pages && pages.length) ||
      (articles && articles.length) ||
      (queries && queries.length);

    let html = "";

    if (hasAny) {
      html += `
        <div class="af-om__seeall af-om__seeall--top">
          <button type="button" class="af-om__seeallBtn" data-af-om-seeall>
            See all results for "${esc(q)}"
          </button>
        </div>`;
    }

    if (queries.length) {
      html += `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Suggestions</div>
          <div class="af-om__chips">
            ${queries.map((query) => {
              const plainText = query.text || "";
              const styledText = query.styled_text || plainText;
              return `
                <button type="button" class="af-om__chip af-om__chip--btn" data-af-om-suggestion="${esc(plainText)}">
                  <span>${styledText}</span>
                </button>
              `;
            }).join("")}
          </div>
        </div>`;
    }

    if (collections.length) {
      html += `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Collections</div>
          <div class="af-om__chips">
            ${collections.map((c) => `
              <a class="af-om__chip" href="${esc(c.url)}">
                ${esc(c.title)}
              </a>
            `).join("")}
          </div>
        </div>`;
    }

    if (pages.length) {
      html += `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Pages</div>
          <div class="af-om__chips">
            ${pages.map((page) => `
              <a class="af-om__chip" href="${esc(page.url)}">
                ${esc(page.title)}
              </a>
            `).join("")}
          </div>
        </div>`;
    }

    if (articles.length) {
      html += `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Blog Posts</div>
          <div class="af-om__chips">
            ${articles.map((article) => `
              <a class="af-om__chip" href="${esc(article.url)}">
                ${esc(article.title)}
              </a>
            `).join("")}
          </div>
        </div>`;
    }

    if (products.length) {
      html += `
        <div class="af-om__rsec">
          <div class="af-om__rsecTitle">Products</div>
          <div class="af-om__pgrid">
            ${products.map((p) => {
              const img =
                (typeof p.image === "string" && p.image) ||
                (p.featured_image && typeof p.featured_image.url === "string" && p.featured_image.url) ||
                (p.image && typeof p.image.url === "string" && p.image.url) || "";

              const price = formatPriceRange(p);

              return `
                <a class="af-om__pcard" href="${esc(p.url)}">
                  <div class="af-om__pmedia">
                    ${img ? `<img class="af-om__pimg" ${thumbAttrs(img)} alt="${esc(p.title)}" loading="lazy">` : ""}
                  </div>
                  <div class="af-om__pinfo">
                    <div class="af-om__ptitle">${esc(p.title)}</div>
                    ${price ? `<div class="af-om__pprice">${price}</div>` : ""}
                  </div>
                </a>`;
            }).join("")}
          </div>

          <div class="af-om__seeall">
            <button type="button" class="af-om__seeallBtn" data-af-om-seeall>
              See all results
            </button>
          </div>
        </div>`;
    }

    if (!hasAny) {
      html = `<div class="af-om__loading">No results for "${esc(q)}".</div>`;
    }

    nodes.predictiveWrap.innerHTML = html;

    // ✅ Recompute scroller + progress after DOM injection
    requestAnimationFrame(() => pickBestScrollerAndUpdate());
  };

  const showPredictive = async (q) => {
    setMode("predictive");
    if (nodes.predictiveTitle) nodes.predictiveTitle.textContent = `Results for "${q}"`;

    const cached = getCachedPredictive(q);
    if (cached) {
      renderPredictive(q, cached);
      return;
    }

    if (nodes.predictiveWrap) nodes.predictiveWrap.innerHTML = buildSkeletonHTML();

    try {
      const data = await fetchPredictive(q);
      // superseded by a newer query, or Enter already opened the full results
      if (q !== state.lastQuery || state.lastMode !== "predictive") return;
      renderPredictive(q, data);
    } catch (err) {
      // aborted by the next keystroke: that request will render, so no error flash
      if (err && err.name === "AbortError") return;
      if (nodes.predictiveWrap) nodes.predictiveWrap.innerHTML = '<div class="af-om__loading">Search failed. Try again.</div>';
      requestAnimationFrame(() => pickBestScrollerAndUpdate());
    }
  };

  const fetchFullSectionHTML = async (q) => {
    const url =
      `${SEARCH_URL}?q=${encodeURIComponent(q)}` +
      `&options[prefix]=last` +
      `&sections=${encodeURIComponent(cfg.fullSectionId)}`;

    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("full section fetch failed");

    const json = await res.json();
    const html = json && json[cfg.fullSectionId];
    if (!html) throw new Error("missing section html");
    return String(html);
  };

  const extractCollectionLinks = (doc) => {
    const out = [];
    const seen = new Set();

    const links = Array.from(doc.querySelectorAll('a[href*="/collections/"]'));
    for (const a of links) {
      const href = a.getAttribute("href") || "";
      const url = normalizeUrl(href);
      if (!url || !url.includes("/collections/")) continue;

      const title = (a.textContent || "").trim().replace(/\s+/g, " ");
      if (!title || title.length < 2) continue;

      if (seen.has(url)) continue;
      seen.add(url);

      out.push({ url: href, title });
      if (out.length >= cfg.fullCollectionsLimit) break;
    }
    return out;
  };

  const extractProductsFromSection = (html) => {
    const doc = domFromHTML(html);

    const scopes = [
      doc.querySelector("#ProductGridContainer"),
      doc.querySelector(".product-grid"),
      doc.querySelector(".template-search__results"),
      doc.body,
    ].filter(Boolean);

    const results = [];
    const seen = new Set();

    const pickText = (el) => (el ? (el.textContent || "").trim().replace(/\s+/g, " ") : "");

    const pickImg = (card) => {
      const img = card.querySelector("img");
      if (!img) return "";
      return (
        img.getAttribute("src") ||
        img.getAttribute("data-src") ||
        (img.getAttribute("srcset") ? img.getAttribute("srcset").split(",")[0].trim().split(" ")[0] : "") ||
        ""
      );
    };

    const pickPrice = (card) => {
      const priceItem = card.querySelector(".price-item:not([hidden])") ||
                       card.querySelector(".price__regular") ||
                       card.querySelector(".price__sale");

      if (priceItem) {
        const t = pickText(priceItem);
        if (t && t.match(/[০-৯0-9]/)) return t.trim();
      }

      const priceEl = card.querySelector(".price") || card.querySelector("[class*='price']");
      if (!priceEl) return "";

      let text = pickText(priceEl);
      text = text.replace(/Regular price|Sale price|From|To|৳|Tk|BDT/gi, " ");
      text = text.replace(/[^\d০-৯৳$€£¥₹,.\s-]/g, " ");
      text = text.replace(/\s+/g, " ").trim();

      const hasSymbol = pickText(priceEl).match(/৳|Tk/);
      if (hasSymbol && !text.includes("৳")) text = "৳ " + text;

      return text || "";
    };

    const pickTitle = (card, link) => {
      const tEl =
        card.querySelector(".card__heading") ||
        card.querySelector(".card__information .full-unstyled-link") ||
        card.querySelector(".full-unstyled-link") ||
        card.querySelector(".card__heading a") ||
        null;

      const t = pickText(tEl);
      if (t) return t;

      const fromLink = pickText(link);
      return fromLink || "Product";
    };

    for (const scope of scopes) {
      const cards = Array.from(scope.querySelectorAll(".card-wrapper, .product-card-wrapper, .grid__item, li, article, div"));
      for (const card of cards) {
        const link = card.querySelector('a[href*="/products/"]');
        if (!link) continue;

        const href = link.getAttribute("href") || "";
        const norm = normalizeUrl(href);
        if (!norm || !norm.includes("/products/")) continue;

        if (seen.has(norm)) continue;
        seen.add(norm);

        const title = pickTitle(card, link);
        const img = pickImg(card);
        const price = pickPrice(card);

        results.push({ url: href, title, img, price });

        if (results.length >= cfg.fullLimit) break;
      }
      if (results.length >= cfg.fullLimit) break;
    }

    return { doc, products: results };
  };

  const renderFull = async (q) => {
    if (!nodes.fullWrap) return;

    setMode("full");
    if (nodes.fullTitle) nodes.fullTitle.textContent = `Results for "${q}"`;
    nodes.fullWrap.innerHTML = '<div class="af-om__loading">Loading results…</div>';

    try {
      const html = await fetchFullSectionHTML(q);
      const { doc, products } = extractProductsFromSection(html);
      const cols = extractCollectionLinks(doc);

      const colHtml = cols.length
        ? `
          <div class="af-om__rsec">
            <div class="af-om__rsecTitle">Collections</div>
            <div class="af-om__chips">
              ${cols.map((c) => `<a class="af-om__chip" href="${esc(c.url)}">${esc(c.title)}</a>`).join("")}
            </div>
          </div>
        `
        : "";

      const prodHtml = products.length
        ? `
          <div class="af-om__rsec">
            <div class="af-om__rsecTitle">Products</div>
            <div class="af-om__pgrid">
              ${products.map((p) => `
                <a class="af-om__pcard" href="${esc(p.url)}">
                  <div class="af-om__pmedia">
                    ${p.img ? `<img class="af-om__pimg" ${thumbAttrs(p.img)} alt="${esc(p.title)}" loading="lazy">` : ""}
                  </div>
                  <div class="af-om__pinfo">
                    <div class="af-om__ptitle">${esc(p.title)}</div>
                    ${p.price ? `<div class="af-om__pprice">${esc(p.price)}</div>` : ""}
                  </div>
                </a>
              `).join("")}
            </div>

            <div class="af-om__seeall">
              <a class="af-om__seeallBtn" data-af-om-open-search-page href="${esc(`${SEARCH_URL}?q=${encodeURIComponent(q)}`)}">
                Open search page
              </a>
            </div>
          </div>
        `
        : `<div class="af-om__loading">No results for "${esc(q)}".</div>`;

      nodes.fullWrap.innerHTML = `<div class="af-om__results">${colHtml}${prodHtml}</div>`;

      // ✅ Recompute scroller + progress after DOM injection
      requestAnimationFrame(() => pickBestScrollerAndUpdate());
    } catch {
      nodes.fullWrap.innerHTML = `
        <div class="af-om__loading">
          Could not load full results.
          <a href="${esc(`${SEARCH_URL}?q=${encodeURIComponent(q)}`)}" data-af-om-open-search-page>Open search page</a>
        </div>`;

      // ✅ Recompute scroller + progress after DOM injection
      requestAnimationFrame(() => pickBestScrollerAndUpdate());
    }
  };

  const openFullFromCurrentQuery = () => {
    const q = (nodes.input ? nodes.input.value.trim() : "");
    if (!q) return;
    // a pending predictive search must not switch the view back after Enter
    window.clearTimeout(state.debounceT);
    if (state.inflight && typeof state.inflight.abort === "function") state.inflight.abort();
    state.lastQuery = q;
    saveRecent(q);
    renderFull(q);
  };

  const onQueryChange = (qRaw) => {
    const q = (qRaw || "").trim();
    const has = q.length > 0;

    if (nodes.clear) nodes.clear.hidden = !has;

    if (!has) {
      state.lastQuery = "";
      setMode("menu");
      return;
    }

    if (q.length < cfg.minChars) {
      setMode("menu");
      return;
    }

    window.clearTimeout(state.debounceT);
    state.debounceT = window.setTimeout(() => {
      if (q === state.lastQuery && state.lastMode === "predictive") return;
      state.lastQuery = q;
      showPredictive(q);
    }, cfg.debounceMs);
  };

  if (nodes.input) {
    nodes.input.addEventListener("input", (e) => onQueryChange(e.target.value), { passive: true });

    nodes.input.addEventListener("focus", () => {
      if (state.suppressFocus) return;
      if (state.lastMode === "menu") return;
      if (!nodes.input.value.trim()) renderRecentSuggestions();
    });
  }

  if (nodes.clear && nodes.input) {
    nodes.clear.addEventListener("click", () => {
      nodes.input.value = "";
      nodes.input.focus({ preventScroll: true });
      onQueryChange("");
    });
  }

  if (nodes.form && nodes.input) {
    nodes.form.addEventListener("submit", (e) => {
      e.preventDefault();
      openFullFromCurrentQuery();
    });
  }

  root.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-af-om-seeall]");
    if (!btn) return;
    e.preventDefault();
    openFullFromCurrentQuery();
  });

  root.addEventListener("click", (e) => {
    const a = e.target.closest("[data-af-om-open-search-page]");
    if (!a) return;
    close();
  });

  root.addEventListener("click", (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (!href) return;
    if (href.startsWith("#")) return;
    // Phones: step back over the menu's entries first, then navigate once the
    // pop has landed, so Back on the next page returns here, not to a dead
    // menu entry. (history.back() during a navigation cancels it on Android.)
    const n = ourHistDepth();
    const plain = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    if (n && plain && isOpen() && !e.defaultPrevented && (!a.target || a.target === "_self")) {
      e.preventDefault();
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        window.location.assign(a.href);
      };
      window.addEventListener("popstate", go, { once: true });
      setTimeout(go, 1000);
      closeInternal(false);
      try { history.go(-n); } catch { go(); }
      return;
    }
    // iOS TOUCH FIX: defer so iOS Safari commits navigation before teardown.
    // ANDROID FIX: use closeInternal(false) instead of close() — close() calls
    // closeMobileSubview(false) which calls history.back(), and history.back()
    // fired during an in-progress navigation cancels it on Android Chrome.
    // closeInternal calls closeMobileSubview(true) (skip history back) so
    // navigation completes correctly on both iOS and Android.
    setTimeout(() => closeInternal(false), 60);
  });

  setPromosForActiveState();
  setMode("menu");
})();